// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState } from 'react';
import { func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import useRouter from 'Hooks/useRouter';
import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import useAsyncError from 'Hooks/useAsyncError';
import useAnalytics from 'Hooks/useAnalytics';
import { API_ACCOUNT_PROFILE, API_ACCOUNT_PASSWORD, API_MFA_DISABLE, EMAIL_REGEX } from 'Constants';
import AccountProfileComponent from 'Components/Account/AccountProfile';

const defaultValuesEmail = {
  email: '',
};

const defaultValues = {
  oldPassword: '',
  password: '',
  confirmPassword: '',
};

const defaultValuesReason = {
  reason: '',
};

const AccountProfile = memo(({ setErrors }) => {
  const [profile, setProfile] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitPasswordLoading, setSubmitPasswordLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [disableMFAloading, setDisableMFAloading] = useState(false);
  const [disableMFAError, setDisableMFAError] = useState([]);
  const fetcher = useFetch();
  const { t } = useTranslation();
  const throwError = useAsyncError();
  const { data: { email, loggedIn, isImpersonated } = {}, userLogout } = useUser();
  const { isAdmin } = usePermissions();
  const { query, location } = useRouter();
  const analytics = useAnalytics();
  const {
    formState: { errors: rhfErrorsEmail },
    register: registerEmail,
    reset: resetEmail,
    getValues: getValuesEmail,
    trigger: triggerEmail,
  } = useForm({
    defaultValuesEmail,
    mode: 'onChange',
    shouldFocusError: true,
  });

  const {
    formState: { errors: rhfErrors },
    register,
    reset,
    watch,
    getValues,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    shouldFocusError: true,
  });

  const {
    formState: { errors: rhfErrorsReason },
    register: registerReason,
    reset: resetReason,
    watch: watchReason,
    trigger: triggerReason,
  } = useForm({
    defaultValuesReason,
    mode: 'onChange',
  });

  const passwordValue = watch('password');
  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });
  const pattern = value => ({ value, message: t('errors.filedInvalidFormat') });

  const validatorsEmail = {
    email: {
      required,
      maxLength: maxLength(40),
      pattern: {
        value: EMAIL_REGEX,
        message: t('errors.invalidEmail'),
      },
    }
  };

  const validators = {
    oldPassword: {
      required,
      minLength: minLength(5),
      maxLength: maxLength(50),
    },
    password: {
      required,
      minLength: minLength(8),
      maxLength: maxLength(50),
      pattern: pattern(/[^\w\d]*(([0-9]+.*[A-Za-z]+.*)|[A-Za-z]+.*([0-9]+.*))/),
    },
    confirmPassword: {
      required,
      minLength: minLength(8),
      maxLength: maxLength(50),
      pattern: pattern(/[^\w\d]*(([0-9]+.*[A-Za-z]+.*)|[A-Za-z]+.*([0-9]+.*))/),
      validate: value => (value !== (passwordValue || '')) ? t('errors.passwordsDoNotMatch') : true,
    },
  };

  const reasonValue = watchReason('reason');

  const validatorsReason = {
    reason: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(255),
    },
  };

  const handleOnModalOpen = () => {
    analytics.track('disable_mfa_modal_open');
    setModalOpen(true);
  };

  const handleOnModalClose = () => {
    analytics.track('disable_mfa_modal_close');
    setModalOpen(false);
  };

  const getAccountProfile = () => {
    setLoading(true);

    fetcher(API_ACCOUNT_PROFILE).then(res => {
      setProfile({
        userId: res?.account?.userId,
        cookiePolicy: res?.account?.cookiePolicy,
        isTotpEnabled: res?.account?.isTotpEnabled,
        isImpersonated: res?.account?.isImpersonated,
      });
      resetEmail({
        email: res?.account?.email || '',
      });
    }).catch(err => throwError(err))
      .finally(() => setLoading(false));
  };

  // Get Profile data on page load
  useEffect(() => {
    if (loggedIn) {
      getAccountProfile();
    }
  }, [location.pathname, query, email, loggedIn]);

  const handleMFADisable = async () => {
    analytics.track('disable_mfa');
    setDisableMFAError([]);
    await triggerReason();

    const rhfErrorsKeys = Object.keys(rhfErrorsReason);
    if (rhfErrorsKeys.length || reasonValue === '') {
      return;
    }

    setDisableMFAloading(true);
    fetcher(API_MFA_DISABLE, {
      method: 'POST',
      body: JSON.stringify({ reason: reasonValue }),
    })
      .then(() => {
        userLogout();
        resetReason(defaultValuesReason);
        setModalOpen(false);
      })
      .catch(err => setDisableMFAError([{ type: 'error', message: err.info.error }]))
      .finally(() => setDisableMFAloading(false));
  };

  const handleOnSubmit = async () => {
    analytics.track('updated_profile_email');
    await triggerEmail();

    const rhfErrorsKeys = Object.keys(rhfErrorsEmail);
    if (rhfErrorsKeys.length) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const payload = getValuesEmail();

    setSubmitLoading(true);
    fetcher(API_ACCOUNT_PROFILE, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }).then(() => {
      getAccountProfile();
      setErrors([{ type: 'success', message: t('profile.profileSaved') }]);
    }).catch(() => setErrors([{ type: 'error', message: t('profile.profileSavedError') }]))
      .finally(() => {
        setSubmitLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  const handleOnSubmitPassword = async () => {
    analytics.track('updated_profile_password');
    await trigger();

    const {
      oldPassword,
      password,
      confirmPassword,
    } = getValues();

    if (!oldPassword || !password || !confirmPassword) {
      return;
    }

    const rhfErrorsKeys = Object.keys(rhfErrors);
    if (rhfErrorsKeys.length) {
      return;
    }

    const parsedPayload = {
      oldPassword,
      newPassword: {
        password,
        passwordConfirmation: confirmPassword,
      }
    };

    setSubmitPasswordLoading(true);
    fetcher(API_ACCOUNT_PASSWORD, {
      method: 'PATCH',
      body: JSON.stringify(parsedPayload),
    }).then(() => {
      reset();
      setErrors([{ type: 'success', message: t('profile.profileSaved') }]);
    }).catch(err => setErrors([{ message: err.info.errors.map(i => i?.message).join('\r\n'), type: 'error' }]))
      .finally(() => {
        setSubmitPasswordLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  return (
    <AccountProfileComponent
      loading={ loading }
      isAdmin={ isAdmin }
      validatorsEmail={ validatorsEmail }
      registerEmail={ registerEmail }
      rhfErrorsEmail={ rhfErrorsEmail }
      validators={ validators }
      register={ register }
      rhfErrors={ rhfErrors }
      handleOnSubmit={ handleOnSubmit }
      handleOnSubmitPassword={ handleOnSubmitPassword }
      submitLoading={ submitLoading }
      submitPasswordLoading={ submitPasswordLoading }
      registerReason={ registerReason }
      validatorsReason={ validatorsReason }
      reasonValue={ reasonValue }
      rhfErrorsReason={ rhfErrorsReason }
      modalOpen={ modalOpen }
      onModalClose={ handleOnModalClose }
      disableMFAloading={ disableMFAloading }
      disableMFAError={ disableMFAError }
      onModalOpen={ handleOnModalOpen }
      handleMFADisable={ handleMFADisable }
      isTotpEnabled={ profile?.isTotpEnabled }
      isImpersonated={ isImpersonated }
    />
  );
});

AccountProfile.propTypes = {
  setErrors: func,
};

AccountProfile.displayName = 'AccountProfileContainer';

export default AccountProfile;
